import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    WeeklyMealPlanV2,
    WeeklyMealPlanLegacy,
    WeeklyMealPlanItemV2,
    isMealPlanV2,
} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {
    WeeklyStateLabelComponent
} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-state-label/weekly-state-label.component';

export type WeeklyMealPlanSideKey = 'challenges' | 'wins';

export type WeeklyMealPlanSideState = 'v2Items' | 'legacyItems' | 'nothingThisWeek' | 'noAnswer';

export interface WeeklyMealPlanSideView {
    state: WeeklyMealPlanSideState;
    items: WeeklyMealPlanItemV2[];
    legacyLabels: string[];
    somethingElse: string | null;
}

const NO_ANSWER_VIEW: WeeklyMealPlanSideView = {
    state: 'noAnswer',
    items: [],
    legacyLabels: [],
    somethingElse: null,
};

/**
 * Carer weekly summary — "Meal plan" / "This week's eating" panel (UNIVAPLAT-3037),
 * reworked from the legacy string[]-only `WeeklySnapshot_MealPlanCheck` shape to the
 * v2 `WeeklyMealPlanV2` contract (UNIVAPLAT-2961 PR #987, pinned on the UNIVAPLAT-2962
 * "Frontend Implementation Plan" comment): each side (`challenges`/`wins`) is
 * `{items: [{label, note}], somethingElse, nothingThisWeek}`. The full item list is
 * rendered here — no 3-item cap. (The dashboard's capped, labels-only card is a
 * different component on a different page/endpoint — `cdss-patient-overview-v2-meal-plan`,
 * UNIVAPLAT-3041, `GET /statistics/weekly`.)
 *
 * Five-state-style rendering per side (never a blank panel, per the Epic's DCB0129
 * hazard log):
 *  - `v2Items`         — at least one item and/or a `somethingElse` line to show.
 *                        Each item's `label` renders with its `note` underneath when
 *                        non-null; `somethingElse` renders as its own trailing line.
 *  - `legacyItems`      — pre-2.9.0 MPC weeks send `challenges`/`wins` as plain
 *                        `string[]` (`WeeklyMealPlanLegacy`) — labels only, no notes.
 *  - `nothingThisWeek`  — the carer explicitly marked nothing to report on this side
 *                        (affirmative, green `nothingToReport` state) — clinically
 *                        distinct from missing data.
 *  - `noAnswer`         — the whole `mealPlan` is null (carer never reached this step)
 *                        or a v2 side has no items/somethingElse/nothingThisWeek flag
 *                        set — genuinely missing data, rendered in the neutral
 *                        `noCheckIn` style, not the affirmative one.
 *
 * PHI (UNIVAPLAT-2962 Epic DCB0129 hazard log): `note` and `somethingElse` are
 * carer-authored free text. Rendered with plain Angular interpolation only — never
 * `innerHTML`, never `console.*`, never interpolated into a toast/error message or
 * sent to analytics.
 *
 * Title copy ("Meal plan" vs "This week's eating") is resolved by the page via
 * `resolveWeeklyCarerCopy(isArfid)` (UNIVAPLAT-3053 `careProgramScope`,
 * UNIVAPLAT-2962 OQ-FE2) and rendered by the page's own `section-title`, matching
 * every sibling panel (Surveys, Daily check-ins, Chapter progress) — this component
 * only renders the panel body, it does not own a title of its own.
 */
@Component({
    selector: 'cdss-weekly-meal-plan-check',
    standalone: true,
    imports: [CommonModule, WeeklyStateLabelComponent],
    templateUrl: './weekly-meal-plan-check.component.html',
    styleUrl: './weekly-meal-plan-check.component.scss'
})
export class WeeklyMealPlanCheckComponent {

    @Input() mealPlan: WeeklyMealPlanV2 | WeeklyMealPlanLegacy | null = null;

    get challengesView(): WeeklyMealPlanSideView {
        return this.resolveSide('challenges');
    }

    get winsView(): WeeklyMealPlanSideView {
        return this.resolveSide('wins');
    }

    trackByIndex(index: number): number {
        return index;
    }

    private resolveSide(side: WeeklyMealPlanSideKey): WeeklyMealPlanSideView {
        if (!this.mealPlan) {
            return NO_ANSWER_VIEW;
        }

        if (isMealPlanV2(this.mealPlan)) {
            const sideData = this.mealPlan[side];
            const somethingElse = sideData.somethingElse ?? null;
            if (sideData.items.length > 0 || somethingElse) {
                return {state: 'v2Items', items: sideData.items, legacyLabels: [], somethingElse};
            }
            if (sideData.nothingThisWeek) {
                return {state: 'nothingThisWeek', items: [], legacyLabels: [], somethingElse: null};
            }
            return NO_ANSWER_VIEW;
        }

        // Legacy (pre-2.9.0 MPC) weeks: plain string[] per side, labels only.
        const legacyLabels = this.mealPlan[side];
        if (legacyLabels.length > 0) {
            return {state: 'legacyItems', items: [], legacyLabels, somethingElse: null};
        }
        return NO_ANSWER_VIEW;
    }
}
